import { ComponentProps, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CloseIcon, HStack, Pressable, ScrollView, VStack, WarningIcon } from 'native-base';

import { Button, Text } from '@pimm/base';
import { Lock01Icon, PencilIcon } from '@pimm/base';
import { formatLastModified } from '../utils';
import { ContactSupportLink, ContactSupportLinkProps } from './contact-support-link';

type ErrorBannerProps = {
  message?: string;
  onDismiss?: () => void;
};

const ErrorBanner = ({ message, ..._props }: ErrorBannerProps) => {
  return (
    <HStack alignItems="center" justifyContent="space-between" px={4} py={2} bg="error.50" borderBottomWidth={1} borderColor="error.200">
      <HStack alignItems="center" space={2} flex={1}>
        <WarningIcon size={4} color="error.700" />
        <Text size="md" fontWeight={500} color="error.700" flex={1}>
          {message}
        </Text>
      </HStack>

      {!!_props.onDismiss && (
        <Pressable onPress={_props.onDismiss}>
          <CloseIcon size={3} color="error.700" />
        </Pressable>
      )}
    </HStack>
  );
};

type FormActionBarProps = {
  countDirtyFields?: number;
  isDisabled?: boolean;
  isLoading?: boolean;
  onCancel?: () => void;
  onSave?: () => void;
};

const FormActionBar = ({ countDirtyFields = 0, isDisabled, isLoading, ..._props }: FormActionBarProps) => {
  const { t } = useTranslation();
  return (
    <HStack alignItems="center" space={3} justifyContent="flex-end">
      {countDirtyFields > 0 && !isLoading && (
        <HStack alignItems="center" space={1.5}>
          <Box w={2} h={2} rounded="full" bg="blue.500" />
          <Text size="md" fontWeight={500} color="blue.700" lineHeight="xs">
            {countDirtyFields} {t(countDirtyFields === 1 ? 'store_settings:unsaved_changes_one' : 'store_settings:unsaved_changes_other')}
          </Text>
        </HStack>
      )}

      <Button
        variant="unstyled"
        size="sm"
        shadow={0}
        minWidth={90}
        borderColor="gray.300"
        bg="white"
        isDisabled={isLoading}
        _text={{ fontSize: 'md' }}
        onPress={_props.onCancel}
      >
        {t('store_settings:cancel')}
      </Button>

      <Button
        size="sm"
        shadow={0}
        minWidth={90}
        isDisabled={isDisabled}
        isLoading={!!isLoading}
        isLoadingText={t('store_settings:saving')}
        _spinner={{ size: 16, color: 'black' }}
        _text={{ fontSize: 'md' }}
        onPress={_props.onSave}
      >
        {t('store_settings:save')}
      </Button>
    </HStack>
  );
};

type CardProps = PropsWithChildren & {
  _content?: ComponentProps<typeof Box>;
  _error?: ErrorBannerProps;
  _formActionBar?: FormActionBarProps;
  title: string;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  isEditing?: boolean;
  variant?: 'plain' | 'form';
  onEdit?: () => void;
  // Arbitrary header-right content independent of the form edit affordance —
  // e.g. Promote/Demote action buttons on a card that isn't itself editable.
  headerRight?: React.ReactNode;
};

export const Card = ({
  _content,
  _error,
  _formActionBar,
  title,
  subtitle,
  icon,
  children,
  isEditing,
  variant = 'plain',
  headerRight,
  ..._props
}: CardProps) => {
  const { t } = useTranslation();

  return (
    <Box shadow={0} bg="white" rounded="lg" borderWidth={1} borderColor="gray.200" overflow="hidden">
      <HStack alignItems="center" px={4} py={3} borderBottomWidth={1} borderColor="gray.200">
        <HStack flex={1} alignItems="center" space={2}>
          {!!icon && icon}
          <VStack space={0.5}>
            <Text size="md" fontWeight={600} color="gray.700" lineHeight="xs">
              {title}
            </Text>
            {!!subtitle && subtitle}
          </VStack>
        </HStack>

        {variant === 'form' && (
          <HStack alignItems="center">
            {!_props.onEdit && !isEditing && (
              <HStack
                alignSelf="flex-start"
                justifyContent="center"
                alignItems="center"
                space={1}
                pl={1.5}
                pr={2}
                py={1}
                rounded="md"
                bg="gray.100"
              >
                <Lock01Icon size={4} color="gray.600" />
                <Text size="xs" fontWeight={600} color="gray.600">
                  {t('store_settings:read_only')}
                </Text>
              </HStack>
            )}

            {!!_props.onEdit && !isEditing && (
              <Pressable onPress={_props.onEdit}>
                <HStack alignItems="center" space={1} px={2} py={1} rounded="md">
                  <PencilIcon size={3} color="gray.700" />
                  <Text size="sm" fontWeight={600} color="gray.700">
                    {t('store_settings:edit')}
                  </Text>
                </HStack>
              </Pressable>
            )}

            {isEditing && <FormActionBar {..._formActionBar} />}
          </HStack>
        )}

        {!!headerRight && !isEditing && headerRight}
      </HStack>

      {!!_error?.message && <ErrorBanner {..._error} />}

      <Box p={4} {..._content}>
        {children}
      </Box>
    </Box>
  );
};

type SectionProps = PropsWithChildren & {
  _error?: ErrorBannerProps;
  _formActionBar?: FormActionBarProps;
  _header?: ComponentProps<typeof HStack>;
  _supportLink?: ContactSupportLinkProps;
  _scrollView?: ComponentProps<typeof ScrollView>;
  lastModified?: string;
  title?: string;
};

export const Section = ({ _error, _formActionBar, _header, _supportLink, _scrollView, children, lastModified, title }: SectionProps) => {
  const { t } = useTranslation();
  // TODO: need to question why date format is fixed in en-US
  const lastModifiedText = formatLastModified(lastModified ?? '', t('store_settings:last_modified'));

  return (
    <VStack flex={1}>
      <HStack alignItems="center" py={1.5} px={4} minH="60px" borderBottomWidth={1} {..._header}>
        <VStack flex={1} space={0.5}>
          {!!title && (
            <Text size="2xl" fontWeight={700} color="black" lineHeight="xs">
              {title}
            </Text>
          )}
          {!!lastModifiedText && (
            <Text size="sm" color="gray.600">
              {lastModifiedText}
            </Text>
          )}
        </VStack>

        {!!_formActionBar?.onSave ? <FormActionBar {..._formActionBar} /> : <ContactSupportLink {..._supportLink} />}
      </HStack>

      {!!_error?.message && <ErrorBanner {..._error} />}

      <ScrollView flex={1} bg="grayCool.100" keyboardShouldPersistTaps="handled" {..._scrollView}>
        {children}
      </ScrollView>
    </VStack>
  );
};

Section.Card = Card;

export default Section;
